import { Plot } from "./plot";
import {
  FixedDimension,
  PercentDimension,
  SingleNamedObject,
  NamedObjectContainer,
  getItemByField,
  SvgRectangleArea,
  Rectangle,
  GraphicPanel,
  roundNumber
} from "../../../../web-graphics";
import { Grid } from "../webgl/grid";
import { Label } from "./label";
import { Meander } from "./meander";
import { getChannelUnitText as _getChannelUnitText } from "../../unit-info-getter";

export class StandardPlot extends Plot {
  constructor(settings) {
    super(settings);
    this.settings.gridCellWidth = this.settings.ppcm;

    this.itemsSettings = {
      signals: Plot.findContentSettings(this.settings, "signals", "central"),
      grid: Plot.findContentSettings(this.settings, "grid", "underCentral"),
      border: Plot.findContentSettings(this.settings, "border", "aboveCentral"),
      meander: Plot.findContentSettings(this.settings, "meander", "leftCentral")
    };

    if (this.signalsBox.content.leftCentral !== undefined) {
      this.leftCentralSvgArea = new SvgRectangleArea(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(0)
          },
          size: {
            width: new PercentDimension(1),
            height: new PercentDimension(1)
          },
          parent: {
            ref: this.signalsBox.content.leftCentral
          },
          htmlObject: {
            parent: {
              ref: this.settings.htmlObject.ref
            },
            graphicParent: this
          }
        }
      );
      this.signalsBox.content.leftCentral.addItem(new SingleNamedObject("SvgLeftCentralArea", this.leftCentralSvgArea));
    }

    this.signalsBox.content.aboveCentral.addItem(
      new SingleNamedObject(
        "Border",
        new Rectangle(
          {
            location: {
              x: new FixedDimension(0, 0),
              y: new FixedDimension(0, 0)
            },
            size: {
              width: new PercentDimension(1),
              height: new PercentDimension(1)
            },
            parent: {
              ref: this.signalsBox.content.aboveCentral
            },
            hasStroke: true,
            strokeColor: this.itemsSettings.border.color
          }
        )
      )
    );

    this.signalsBox.content.underCentral.addItem(
      new SingleNamedObject(
        "Grid",
        new Grid(
          {
            location: {
              x: new FixedDimension(0),
              y: new FixedDimension(0)
            },
            size: {
              width: new PercentDimension(1),
              height: new PercentDimension(1)
            },
            parent: {
              ref: this.signalsBox.content.underCentral
            },
            cellSize: [this.settings.gridCellWidth * this.itemsSettings.signals.viewScale.x, this.settings.gridCellWidth * this.itemsSettings.signals.viewScale.y],
            color: this.itemsSettings.grid.color
          }
        )
      )
    );

    this.dataSourceLeftArea = new NamedObjectContainer("data-sources");
    if (this.itemsSettings.meander !== undefined && this.leftCentralSvgArea !== undefined) {
      let meanderX =
        this.leftCentralSvgArea.settings.sizeInPixels.width - this.itemsSettings.meander.location.x - this.itemsSettings.meander.size.width;
      let meanderY = 0;
      const realScaleY =
        (this.settings.signalsBox.scaleY.currValue * this.itemsSettings.signals.viewScale.y) / this.settings.signalsBox.defaultCellSize_mm;
      this.meander = new Meander(
        {
          location: {
            x: new FixedDimension(meanderX),
            y: new FixedDimension(2)
          },
          size: {
            width: new FixedDimension(this.itemsSettings.meander.size.width),
            height: new FixedDimension(this.settings.gridCellWidth * realScaleY - 1)
          },
          parent: {
            ref: this.leftCentralSvgArea
          },
          htmlObject: {
            parent: {
              ref: this.leftCentralSvgArea.settings.htmlObject.ref
            },
            graphicParent: this.leftCentralSvgArea
          },
          widthTop: this.itemsSettings.meander.size.widthTop,
          title: this.settings.title,
          style: this.itemsSettings.meander.style
        }
      );
      this.meander.settings.htmlObject.ref.style.position = "absolute";
      this.meander.setVisibility(this.itemsSettings.meander.visibility ?? true);
      if (
        this.itemsSettings.meander.channelUnit === undefined &&
        this.settings.dataSources.length > 0 &&
        this.settings.dataSources[0].channels.length > 0
      ) {
        this.itemsSettings.meander.channelUnit = _getChannelUnitText(this.settings.dataSources[0].channels[0], this.settings.common.data);
        this.selectedChannel = {
          id_dataSource: this.settings.dataSources[0].id,
          id_channel: this.settings.dataSources[0].channels[0].id
        };
      }
      let meanderUnit = `${this.itemsSettings.meander.unit}/${this.itemsSettings.meander.channelUnit}`;

      this.meanderLabelPanel = new GraphicPanel(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(meanderY)
          },
          size: {
            width: new PercentDimension(1),
            height: new FixedDimension(20)
          },
          padding: {
            right: new FixedDimension(10)
          },
          parent: {
            ref: this.leftCentralSvgArea
          }
        }
      );

      this.meanderLabel = new Label(
        {
          location: {
            x: new PercentDimension(1),
            y: new FixedDimension(0)
          },
          size: {
            width: new PercentDimension(1),
            height: new PercentDimension(1)
          },
          margin: {
            right: new FixedDimension(10)
          },
          parent: {
            ref: this.meanderLabelPanel
          },
          htmlObject: {
            parent: {
              ref: this.leftCentralSvgArea.settings.htmlObject.ref
            },
            graphicParent: this.leftCentralSvgArea
          },
          title: `${this.settings.signalsBox.scaleY.currValue} ${meanderUnit}`,
          fontSize: this.itemsSettings.meander.label.fontSize,
          fontFamily: this.itemsSettings.meander.label.fontFamily,
          color: this.itemsSettings.meander.label.color,
          isCentered: false
        }
      );
      this.meanderLabel.settings.htmlObject.ref.style.userSelect = "none";
      this.meanderLabel.settings.htmlObject.ref.style.position = "absolute";
      this.meanderLabel.settings.htmlObject.ref.setAttribute("text-anchor", "end");

      this.meanderLabelPanel.items.add(new SingleNamedObject("MeanderLabel", this.meanderLabel));
      this.leftCentralSvgArea.childItemsContainer.add(new SingleNamedObject("Meander", this.meander));
      this.leftCentralSvgArea.childItemsContainer.add(new SingleNamedObject("MeanderLabelPanel", this.meanderLabelPanel));
      this.meanderLabel.setVisibility(this.itemsSettings.meander.visibility ?? true);

      this.leftCentralSvgArea.childItemsContainer.add(this.dataSourceLeftArea);
    }
  }

  afterSignalsCreate(dataSourceId, channelId) {
    const channelLeftArea = this.getChannelLeftArea(dataSourceId, channelId);
    const signalLabels = this._getCreatedChildContainer("signal-labels", channelLeftArea);
    const meanValueLabels = this._getCreatedChildContainer("mean-value-labels", channelLeftArea);

    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    let plotCentralHeight = this.signalsBox.content.central.settings.sizeInPixels.height;
    let channelY = channelSettings.y.calcValue(plotCentralHeight);
    let channelHeight = channelSettings.height.calcValue(plotCentralHeight);

    let currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    let signalLabelsSettings = Plot.findContentSettings(currConfiguration, "signalLabels", "leftCentral")?.settings;

    if (signalLabelsSettings === undefined) {
      return;
    }
    let isolinePositions = new Map();
    for (let labelIndex = 0; labelIndex < signalLabelsSettings.length; labelIndex++) {
      let signalLabelSettings = signalLabelsSettings[labelIndex];

      let isolineY = 0;
      let signalSettings = getItemByField("id", signalLabelSettings.id_signal, currConfiguration.signals);
      let isolineSettings = getItemByField("id", signalSettings.id_isoline, currConfiguration.isolines);
      if (signalLabelSettings.linkedPosition) {
        isolineY = channelY + isolineSettings.y.calcValue(channelHeight);
      }
      let labelY = isolineY + (signalLabelSettings.y?.calcValue(channelHeight) ?? 0);
      isolinePositions.set(signalLabelSettings.id_signal, isolineY);

      let signalLabel = new Label(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(labelY)
          },
          size: {
            width: new PercentDimension(1),
            height: new FixedDimension(20)
          },
          parent: {
            ref: this.leftCentralSvgArea
          },
          htmlObject: {
            parent: {
              ref: this.leftCentralSvgArea.settings.htmlObject.ref
            },
            graphicParent: this.leftCentralSvgArea
          },
          title: signalLabelSettings.text,
          fontSize: this.itemsSettings.signals.labels.fontSize,
          fontFamily: this.itemsSettings.signals.labels.fontFamily,
          color: signalLabelSettings.color
        }
      );
      signalLabel.settings.htmlObject.ref.style.userSelect = "none";

      signalLabel.addOnClickedFunction(() => {
        this.onSignalLabelClicked(dataSourceId, channelId);
      });

      this.linkObjectWithSignal(signalLabel, dataSourceId, channelId, signalLabelSettings.id_signal, isolineY => {
        let labelPosY = 0;
        if (signalLabelSettings.linkedPosition) {
          labelPosY = isolineY;
        }
        labelPosY += signalLabelSettings.y?.calcValue(channelHeight) ?? 0;
        return labelPosY;
      });
      signalLabels.add(new SingleNamedObject(`${signalLabelSettings.id_signal}`, signalLabel));
    }

    let meanValueLabelsSettings = Plot.findContentSettings(currConfiguration, "meanValueLabels", "leftCentral")?.settings;
    if (meanValueLabelsSettings === undefined || this.itemsSettings.meander === undefined) {
      return;
    }
    const unitText = this.getChannelUnitText(channelSettings);
    for (let labelIndex = 0; labelIndex < meanValueLabelsSettings.length; labelIndex++) {
      let meanValueLabelSettings = meanValueLabelsSettings[labelIndex];
      let signalLabelSettings = getItemByField("id_signal", meanValueLabelSettings.id_signal, signalLabelsSettings);
      let isolineY = isolinePositions.get(signalLabelSettings.id_signal);

      let meanValueLabelY = 0;
      if (signalLabelSettings.linkedPosition) {
        meanValueLabelY = isolineY;
      }
      meanValueLabelY += signalLabelSettings.y.calcValue(channelHeight);
      meanValueLabelY += meanValueLabelSettings.y === undefined ? 0 : meanValueLabelSettings.y.calcValue(channelHeight);

      let meanValueLabel = new Label(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(meanValueLabelY)
          },
          size: {
            width: new PercentDimension(1),
            height: new FixedDimension(20)
          },
          parent: {
            ref: this.leftCentralSvgArea
          },
          htmlObject: {
            parent: {
              ref: this.leftCentralSvgArea.settings.htmlObject.ref
            },
            graphicParent: this.leftCentralSvgArea
          },
          title: `0 ${unitText}`,
          fontSize: this.itemsSettings.meander.label.fontSize,
          fontFamily: this.itemsSettings.meander.label.fontFamily,
          color: meanValueLabelSettings.color
        }
      );
      meanValueLabel.settings.htmlObject.ref.style.userSelect = "none";

      meanValueLabel.addOnClickedFunction(() => {
        this.onSignalLabelClicked(dataSourceId, channelId);
      });

      this.linkObjectWithSignal(meanValueLabel, dataSourceId, channelId, signalLabelSettings.id_signal, signalY => {
        let labelPosY = 0;
        if (signalLabelSettings.linkedPosition) {
          labelPosY = signalY;
        }
        labelPosY += signalLabelSettings.y.calcValue(channelHeight);
        return labelPosY + (meanValueLabelSettings.y === undefined ? 0 : meanValueLabelSettings.y.calcValue(channelHeight));
      });
      meanValueLabels.add(new SingleNamedObject(`${signalLabelSettings.id_signal}`, meanValueLabel));
    }
  }

  afterAddPointsToSignal() {}

  afterAddPointsToChannels() {}

  afterChannelsDataClear() {}

  afterChannelDataUpdated() {}

  afterChannelClear(dataSourceId, channelId) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    let configurationSettings = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    const channelLeftArea = this.getChannelLeftArea(dataSourceId, channelId);
    const signalLabels = channelLeftArea.findItem("signal-labels");
    const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
    for (let signalIndex = 0; signalIndex < configurationSettings.signals.length; signalIndex++) {
      let signalId = configurationSettings.signals[signalIndex].id;
      let signalLabelIndex = signalLabels.findIndex(`${signalId}`);
      if (signalLabelIndex >= 0) {
        let signalLabelItem = signalLabels.getItem(signalLabelIndex);
        signalLabelItem.item.removeObjectFromDom();
        signalLabels.remove(signalLabelIndex);
      }
      let meanValueIndex = meanValueLabels.findIndex(`${signalId}`);
      if (meanValueIndex >= 0) {
        let meanValueLabelItem = meanValueLabels.getItem(meanValueIndex);
        meanValueLabelItem.item.removeObjectFromDom();
        meanValueLabels.remove(meanValueIndex);
      }
    }
  }

  setScaleX(newScaleX) {
    super.setScaleX(newScaleX);
  }

  setScaleY(newScaleY) {
    super.setScaleY(newScaleY);
    if (!(this.itemsSettings.meander?.visibility ?? true)) {
      return;
    }
    this.setMeanderHeight(newScaleY);
    this.meanderLabel.setText(`${newScaleY.toString()} ${this.itemsSettings.meander.unit}/${this.itemsSettings.meander.channelUnit}`);
  }

  setChannelScaleY(dataSourceId, channelId, newScaleY) {
    super.setChannelScaleY(dataSourceId, channelId, newScaleY);
    if (!(this.itemsSettings.meander?.visibility ?? true)) {
      return;
    }
    this.setMeanderHeight(newScaleY);
    this.meanderLabel.setText(`${newScaleY.toString()} ${this.itemsSettings.meander.unit}/${this.itemsSettings.meander.channelUnit}`);
  }

  setMeanderHeight(scaleY) {
    //let realScaleY = scaleY / this.settings.signalsBox.defaultCellSize_mm;
    const realScaleY = (scaleY * this.itemsSettings.signals.viewScale.y) / this.settings.signalsBox.defaultCellSize_mm;
    const meanderObject = this.leftCentralSvgArea.childItemsContainer.findItem("Meander");
    meanderObject.item.setHeight(new FixedDimension(this.settings.gridCellWidth * realScaleY - 1));
  }

  setMeanderUnit(unit) {
    if (this.leftCentralSvgArea === undefined || this.itemsSettings.meander === undefined) {
      return;
    }
    this.itemsSettings.meander.channelUnit = unit;
    const channelScaleY = this.getChannelScaleY(this.selectedChannel.id_dataSource, this.selectedChannel.id_channel);
    this.meanderLabel.setText(`${channelScaleY} ${this.itemsSettings.meander.unit}/${this.itemsSettings.meander.channelUnit}`);
  }

  setChannelUnit(dataSourceId, channelId, unit) {
    const channelSettings = this.getChannelSettings(dataSourceId, channelId);
    const channelLeftArea = this.getChannelLeftArea(dataSourceId, channelId);
    const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
    channelSettings.unit.text = unit;
    const configurationSettings = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    configurationSettings.signals.forEach(signalSettings => {
      const meanValueLabel = meanValueLabels.findItem(`${signalSettings.id}`);
      if (meanValueLabel === undefined) {
        return;
      }
      const labelText = meanValueLabel.item.settings.title;
      const labelTextParts = labelText.split(" ");
      if (labelTextParts.length != 2) {
        return;
      }
      meanValueLabel.item.setText(`${labelTextParts[0]} ${channelSettings.unit.text}`);
    });
    if (this.selectedChannel.id_dataSource == dataSourceId && this.selectedChannel.id_channel == channelId) {
      this.setMeanderUnit(channelSettings.unit.text);
    }
  }

  updateMeanValueText(dataSourceId, channelId, signalId, v) {
    const channelLeftArea = this.getChannelLeftArea(dataSourceId, channelId);
    const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
    const meanValueLabel = meanValueLabels.findItem(`${signalId}`);
    if (meanValueLabel !== undefined) {
      this.onMeanValueTextUpdated?.(dataSourceId, channelId, signalId, v);
      const channelSettings = this.getChannelSettings(dataSourceId, channelId);
      const unitScale = this.getChannelUnitScale(channelSettings);
      const valueInUserUnits = roundNumber(v * unitScale, 2);
      const unitText = this.getChannelUnitText(channelSettings);
      meanValueLabel.item.setText(`${valueInUserUnits} ${unitText}`);
    }
  }

  tryUpdateMeanSignalsValue() {
    this.forEachSignal(signalObject => {
      if (signalObject.tryUpdateMeanSignalValue !== undefined) {
        signalObject.tryUpdateMeanSignalValue();
      }
    });
  }

  trySetNewIsoline() {
    this.forEachSignal(signalObject => {
      if (signalObject.trySetNewIsoline !== undefined) {
        signalObject.trySetNewIsoline();
      }
    });
  }

  getChannelLeftArea(dataSourceId, channelId) {
    let dataSourceItems = this.dataSourceLeftArea.findItem(dataSourceId);
    if (dataSourceItems === undefined) {
      dataSourceItems = new NamedObjectContainer(dataSourceId.toString());
      this.dataSourceLeftArea.add(dataSourceItems);
    }
    let channelItems = dataSourceItems.findItem(channelId);
    if (channelItems === undefined) {
      channelItems = new NamedObjectContainer(channelId.toString());
      dataSourceItems.add(channelItems);
    }
    return channelItems;
  }

  getSignalBufferIndex(dataSourceId, channelId, signalid, x) {
    let signalObject = this.getSignalObject(dataSourceId, channelId, signalid);
    return signalObject.getSignalBufferIndex(Math.round(x));
  }

  onSignalLabelClicked(dataSourceId, channelId) {
    this.selectedChannel = {
      id_dataSource: dataSourceId,
      id_channel: channelId
    };
    this.selectChannelLabels(dataSourceId, channelId);
    this.onSelectChannel?.(dataSourceId, channelId);
  }

  setOnSelectChannel(action) {
    this.onSelectChannel = action;
  }

  selectChannelLabels(dataSourceId, channelId) {
    this.settings.dataSources.forEach(dataSourceSettings => {
      dataSourceSettings.channels.forEach(channelSettings => {
        let channelLeftArea = this.getChannelLeftArea(dataSourceSettings.id, channelSettings.id);
        const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
        const signalLabels = channelLeftArea.findItem("signal-labels");
        const isSelected = dataSourceId == dataSourceSettings.id && channelId == channelSettings.id;
        meanValueLabels.forEach(meanValueLabel => {
          meanValueLabel.setIsBold(isSelected);
        });
        signalLabels.forEach(signalLabel => {
          signalLabel.setIsBold(isSelected);
        });
      });
    });
  }

  _getCreatedChildContainer(childName, parentContainer) {
    let childContainer = parentContainer.findItem(childName);
    if (childContainer === undefined) {
      childContainer = new NamedObjectContainer(childName);
      parentContainer.add(childContainer);
    }
    return childContainer;
  }
}
